import { create } from 'zustand';

export type PanelWidth = 'narrow' | 'medium' | 'wide';

export const PANEL_WIDTH_PX: Record<PanelWidth, number> = {
  narrow: 380,
  medium: 480,
  wide: 600,
};

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
}

export interface AnalyzePrompt {
  query: string;
  context?: string;
  anchorRect: DOMRect;
}

interface ChatState {
  isOpen: boolean;
  panelWidth: PanelWidth;
  messages: ChatMessage[];
  isThinking: boolean;

  // Analyze-this popover state
  analyzePrompt: AnalyzePrompt | null;

  toggleChat: () => void;
  setOpen: (open: boolean) => void;
  setPanelWidth: (width: PanelWidth) => void;
  addMessage: (msg: ChatMessage) => void;
  setThinking: (val: boolean) => void;
  clearMessages: () => void;

  // Analyze-this actions
  showAnalyzePrompt: (query: string, context: string, anchorRect: DOMRect) => void;
  dismissAnalyzePrompt: () => void;
  confirmAnalyzePrompt: (finalQuery: string) => void;
  sendAnalyzeQuery: (query: string) => void;
}

export const useChatStore = create<ChatState>()((set) => ({
  isOpen: false,
  panelWidth: 'medium',
  messages: [],
  isThinking: false,
  analyzePrompt: null,

  toggleChat: () => set((s) => ({ isOpen: !s.isOpen })),
  setOpen: (open) => set({ isOpen: open }),
  setPanelWidth: (width) => set({ panelWidth: width }),
  addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
  setThinking: (val) => set({ isThinking: val }),
  clearMessages: () => set({ messages: [] }),

  showAnalyzePrompt: (query, context, anchorRect) =>
    set({ analyzePrompt: { query, context, anchorRect } }),

  dismissAnalyzePrompt: () => set({ analyzePrompt: null }),

  confirmAnalyzePrompt: (finalQuery) => {
    // Bridge from AnalyzeThisPopover -> sendAnalyzeQuery
    useChatStore.getState().sendAnalyzeQuery(finalQuery);
  },

  sendAnalyzeQuery: (query) => {
    const msg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: query,
      timestamp: Date.now(),
    };
    set((s) => ({
      analyzePrompt: null,
      isOpen: true,
      messages: [...s.messages, msg],
      isThinking: true,
    }));

    // Simulate AI response after a short delay
    setTimeout(() => {
      const reply: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: `Based on GA4 Property 484092824 data for The Monarch San Antonio:\n\n**Query:** ${query}\n\nThis analysis would connect to the GA4 Data API to pull real-time metrics. Once the backend agent is configured, I'll provide detailed breakdowns, trend analysis, and actionable recommendations.\n\nReview the pre-loaded data on the dashboard pages for current insights.`,
        timestamp: Date.now(),
      };
      set((s) => ({
        messages: [...s.messages, reply],
        isThinking: false,
      }));
    }, 1500);
  },
}));
